import Button from "@/components/button/Button"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { Col, Collapse, Row, Space } from "antd"
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint"
import { FC, useState } from "react"

import levelsCapitol from "./images/capitol-building.png"
import levelsTower from "./images/transmission-tower.png"
import levelsWorknet from "./images/worknet.png"

import { useAuth } from "@/components/context/AuthProvider"
import { useGeneralContext } from "@/components/context/GeneralContext"
import { SvgIcon } from "@/components/icon"
import { createdProjectId } from "@/helpers/globalVariables"
import { useLocation, useNavigate } from "react-router"
import { useLocalStorage } from "react-use"
import "./WayleaveRequirementsLevel.less"
import LevelsCollapseHeader from "./levels-collapse-header/LevelsCollapseHeader"

const { Title, Paragraph } = WebsiteTypography
const { Panel } = Collapse

export type LevelItem = {
  id: EnumProjectLevelofservices
  title: string
  subtitle: string
  image: string
}

const levels: LevelItem[] = [
  {
    id: "full_wayleave",
    title: "Full Wayleave Submission",
    subtitle:
      "Instantly identify all impacted infrastructure owners, including municipalities, road authorities (provincial and national), railway operators, utility companies, and service providers—along with our channel partners. We handle the entire process by preparing and submitting your application to each relevant party, all seamlessly managed through our portal.",
    image: levelsCapitol,
  },
  {
    id: "service_providers",
    title: "Service Provider Only Submission",
    subtitle:
      "Instantly identify all utility companies and service providers impacted by your project, including our channel partners. We take care of preparing and submitting your application to each affected provider—fully managed and distributed through our portal.",
    image: levelsTower,
  },
  {
    id: "single_submission",
    title: "One Submission to Channel Partners",
    subtitle:
      "Easily submit one application to all service providers and utility companies within the Easetech channel partner network. We handle the distribution by automatically sending your application to all providers whose service areas are impacted by your project—no duplicate submissions required.",
    image: levelsWorknet,
  },
]

const WayleaveRequirementsLevel: FC = () => {
  const { organization } = useAuth()
  const { xxl, xl, lg } = useBreakpoint()
  const { setCurrentLevelOfService } = useGeneralContext()
  const { setIsModalDarkOpen } = useModalDarkContext()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [, setProjectId] = useLocalStorage<string>(createdProjectId)
  const [, setEscLink] = useLocalStorage("esc-link")
  const [openedPanelsIds, setOpenedPanelsIds] = useState<EnumProjectLevelofservices[]>([])

  const [checkedPanelId, setCheckedPanelId] = useState<EnumProjectLevelofservices>(levels[0].id)

  const onCancel = () => {
    setIsModalDarkOpen(false)
  }

  const handleOnContinue = () => {
    setCurrentLevelOfService(checkedPanelId)
    navigate(`/${organization}/dashboard/create-project/details`)
    setProjectId("")
    setIsModalDarkOpen(false)
    setEscLink(pathname)
  }

  const onChange = (key: string | string[]) => {
    setOpenedPanelsIds(key as EnumProjectLevelofservices[])
  }

  return (
    <Row justify={"center"}>
      <Col span={20} xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
        <Title level={4} style={{ paddingBottom: 16 }}>
          <span className={"bold"}>{"Choose your "}</span>
          <span className={"w300"}>{"Wayleave Requirements"}</span>
        </Title>

        <Paragraph color={"website-secondary"} style={{ paddingBottom: 16 }}>
          {"Select the option that best matches your project’s wayleave needs and desired level of service."}
        </Paragraph>

        <div className={"collapse-levels-wrapper"}>
          <Collapse
            // defaultActiveKey={[levels[0].id]}
            onChange={onChange}
            bordered={false}
            expandIconPosition={"end"}
            className={"collapse-levels-custom-collapse"}
            expandIcon={({ isActive }) =>
              isActive ? <SvgIcon type={"button-minus"} /> : <SvgIcon type={"button-plus"} />
            }
            collapsible={"icon"}
          >
            {levels.map(level => {
              return (
                <Panel
                  key={level.id}
                  className={"collapse-levels-custom-panel"}
                  header={
                    <LevelsCollapseHeader
                      level={level}
                      isChecked={level.id === checkedPanelId}
                      setCheckedPanelId={setCheckedPanelId}
                      isOpened={openedPanelsIds}
                    />
                  }
                >
                  <Paragraph size={"sm"} color={"website-secondary"} style={{ paddingLeft: 68, paddingRight: 40 }}>
                    {level.subtitle}
                  </Paragraph>
                </Panel>
              )
            })}
          </Collapse>
        </div>

        <Row justify={"center"} style={{ paddingTop: 24 }}>
          <Space>
            <Button text={"Cancel".toUpperCase()} btnType={"text"} textColor={"orange"} onClick={onCancel} />
            <Button btnType={"primary"} text={"CONTINUE"} onClick={handleOnContinue} />
          </Space>
        </Row>
      </Col>
    </Row>
  )
}

export default WayleaveRequirementsLevel
